import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Ban, Copy, Check, AlertTriangle, Eye, EyeOff, Shield } from '../../icons/wp-icons';
import { apiKeyApi } from '../../api/apiKeyApi';

// Deutsche Labels für Scopes
const SCOPE_LABELS = {
  KUNDEN: 'Kunden',
  ANFRAGEN: 'Anfragen',
  TERMINE: 'Termine',
  RECHNUNGEN: 'Rechnungen',
  KOSTENVORANSCHLAEGE: 'Kostenvoranschläge',
  ZEITERFASSUNG: 'Zeiterfassung',
  KATALOG: 'Katalog',
  PROJEKTE: 'Projekte',
  BESTELLUNGEN: 'Bestellungen'
};

function ApiKeySettings({ readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Formular für neuen Key
  const [form, setForm] = useState({
    name: '',
    scopes: [],
    expiresAt: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [keysData, scopesData] = await Promise.all([
        apiKeyApi.list(),
        apiKeyApi.getScopes()
      ]);
      setKeys(keysData);
      setAvailableScopes(scopesData);
    } catch (error) {
      console.error('Fehler beim Laden der API-Keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    if (form.scopes.length === 0) return;

    setCreating(true);
    try {
      const result = await apiKeyApi.create({
        name: form.name.trim(),
        scopes: form.scopes,
        expiresAt: form.expiresAt || undefined
      });
      setNewKeyResult(result);
      setForm({ name: '', scopes: [], expiresAt: '' });
      // Keys neu laden (ohne den raw key)
      const keysData = await apiKeyApi.list();
      setKeys(keysData);
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await apiKeyApi.revoke(id);
      setKeys(prev => prev.map(k => k.id === id ? { ...k, aktiv: false } : k));
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Widerrufen');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiKeyApi.delete(id);
      setKeys(prev => prev.filter(k => k.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = key;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleScope = (scope) => {
    setForm(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  const selectAllScopes = () => {
    setForm(prev => ({ ...prev, scopes: [...availableScopes] }));
  };

  const clearScopes = () => {
    setForm(prev => ({ ...prev, scopes: [] }));
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-primary" />
            <div>
              <h3 className="text-lg font-semibold">API-Schlüssel</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Verwalten Sie API-Keys für externe Integrationen (z.B. HeyKiki, EasyFirma)
              </p>
            </div>
          </div>
          {!readOnly && !showCreate && !newKeyResult && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition text-sm"
            >
              <Plus className="w-4 h-4" />
              Neuer API-Key
            </button>
          )}
        </div>
      </div>

      {/* Neuen Key erstellt - Anzeige (nur einmalig sichtbar!) */}
      {newKeyResult && (
        <div className="p-6 border-b border-border bg-green-50">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-green-800">
                API-Key "{newKeyResult.name}" erstellt
              </h4>
              <p className="text-sm text-green-700 mt-1 mb-3">
                Kopieren Sie den Key jetzt. Er wird aus Sicherheitsgründen nicht erneut angezeigt.
              </p>
              <div className="flex items-center gap-2 bg-white border border-green-200 rounded-lg p-3">
                <code className="flex-1 text-sm font-mono break-all select-all">
                  {newKeyResult.key}
                </code>
                <button
                  onClick={() => handleCopyKey(newKeyResult.key)}
                  className="flex-shrink-0 p-2 hover:bg-green-50 rounded transition"
                  title="Kopieren"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              </div>
              <button
                onClick={() => setNewKeyResult(null)}
                className="mt-3 text-sm text-green-700 hover:text-green-900 underline"
              >
                Verstanden, schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Neuen Key erstellen - Formular */}
      {showCreate && !newKeyResult && (
        <div className="p-6 border-b border-border bg-blue-50/50">
          <h4 className="font-semibold mb-4">Neuen API-Key erstellen</h4>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name der Integration *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. HeyKiki, EasyFirma, Zapier"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            />
          </div>

          {/* Scopes */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Berechtigungen (Scopes) *
              </label>
              <div className="flex gap-2">
                <button
                  onClick={selectAllScopes}
                  className="text-xs text-primary hover:underline"
                >
                  Alle auswählen
                </button>
                <span className="text-xs text-gray-300">|</span>
                <button
                  onClick={clearScopes}
                  className="text-xs text-gray-500 hover:underline"
                >
                  Alle entfernen
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {availableScopes.map(scope => (
                <label
                  key={scope}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition text-sm ${
                    form.scopes.includes(scope)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    className="sr-only"
                  />
                  <Shield className={`w-3.5 h-3.5 ${form.scopes.includes(scope) ? 'text-primary' : 'text-gray-400'}`} />
                  {SCOPE_LABELS[scope] || scope}
                </label>
              ))}
            </div>
            {form.scopes.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Mindestens ein Scope erforderlich</p>
            )}
          </div>

          {/* Ablaufdatum (optional) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ablaufdatum (optional)
            </label>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm(prev => ({ ...prev, expiresAt: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leer lassen für unbegrenzten Key
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creating || !form.name.trim() || form.scopes.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition text-sm disabled:opacity-50"
            >
              {creating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Key className="w-4 h-4" />
              )}
              API-Key erstellen
            </button>
            <button
              onClick={() => { setShowCreate(false); setForm({ name: '', scopes: [], expiresAt: '' }); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Key-Liste */}
      <div className="divide-y divide-border">
        {keys.length === 0 && !showCreate && !newKeyResult ? (
          <div className="p-8 text-center text-gray-500">
            <Key className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Keine API-Keys vorhanden</p>
            <p className="text-sm mt-1">
              Erstellen Sie einen API-Key für sichere Machine-to-Machine Integrationen.
            </p>
          </div>
        ) : (
          keys.map(apiKey => (
            <div key={apiKey.id} className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Name + Status */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{apiKey.name}</span>
                    {apiKey.aktiv ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Widerrufen
                      </span>
                    )}
                    {apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date() && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Abgelaufen
                      </span>
                    )}
                  </div>

                  {/* Key Prefix */}
                  <code className="text-xs text-gray-500 font-mono">{apiKey.keyPrefix}</code>

                  {/* Scopes */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(apiKey.scopes || []).map(scope => (
                      <span
                        key={scope}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                      >
                        {SCOPE_LABELS[scope] || scope}
                      </span>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-400">
                    <span>
                      Erstellt: {new Date(apiKey.createdAt).toLocaleDateString('de-DE')}
                    </span>
                    {apiKey.lastUsedAt && (
                      <span>
                        Zuletzt verwendet: {new Date(apiKey.lastUsedAt).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    )}
                    {apiKey.expiresAt && (
                      <span>
                        Läuft ab: {new Date(apiKey.expiresAt).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {!readOnly && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {apiKey.aktiv && (
                      <button
                        onClick={() => handleRevoke(apiKey.id)}
                        className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition"
                        title="Widerrufen"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                    {deleteConfirm === apiKey.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(apiKey.id)}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition"
                        >
                          Löschen
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition"
                        >
                          Nein
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(apiKey.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Hinweis */}
      <div className="p-4 border-t border-border bg-gray-50/50 rounded-b-card">
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-600">Sicherheitshinweis</p>
            <p className="mt-0.5">
              API-Keys gewähren Zugriff auf Ihre Daten. Teilen Sie Keys nur mit vertrauenswürdigen
              Integrationen und widerrufen Sie nicht mehr benötigte Keys sofort.
              Jeder Request mit X-API-Key Header wird authentifiziert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiKeySettings;
